import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  Portal,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  faCheck,
  faChevronDown,
  faSitemap,
  faTableList,
  faRss,
} from '@fortawesome/free-solid-svg-icons';

import { find, flatMap, groupBy, map, uniq } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import {
  CreateOrderDocument,
  CreateOrderInput,
  CreateTableFromTemplateDocument,
  GetProjectTemplatesPageDocument,
  OrderDeepFragment,
  OrderStatus,
  ProjectDeepFragment,
  GetSharedLinksDocument,
  SharedLinkDeepFragment,
  EventType,
  EventResource,
  ColumnType,
  PredicateType,
  UpdateOrderDocument,
  ProtanProjectStepStatusEnum,
  ProtanProjectStepEnum,
} from '../../../gql/graphql';
import { useLocalStorage } from '../../../hooks/local-storage';
import { ResolvedDbIdsReturn } from '../../../hooks/order';
import { parseLabels } from '../../../utils/label-utils';
import SparkelIcon from '../../common/icon/SparkelIcon';

import {
  EventName,
  EventProperties,
  useMixpanel,
} from '../../../services/mixpanel';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../../common/Panel';
import PlaceholderText from '../../text/PlaceholderText';
import { useUserTenant } from '../../../services/auth-info';
import { Buy } from '../project-modal/Buy';
import { tierLimits } from '../../../utils/tier-utils';
import { getHistoricalOrders } from '../project-history/hooks/useDiffing';
import { useProjectHistory } from '../project-history/hooks/useProjectHistory';
import NewOrderModal from './NewOrderModal';
import OrderItem, { OrderItemDiff } from './OrderItem';
import { useEventHistory } from 'src/hooks/event-history';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import ProtanUploadModal from 'src/components/orderContractor/ProtanUploadModal';
import { useProtanProjectSteps } from 'src/components/common/ProtanProjectStepsProvider';
import { useShapeFolders } from 'src/hooks/shape-folders';

type OrdersPanelProps = {
  orders: OrderDeepFragment[];
  project: ProjectDeepFragment;
  resolvedDbIds: ResolvedDbIdsReturn;
  isContractor: boolean;
  readOnly: boolean;
};

export const NO_LABEL_PLACEHOLDER = 'NO_LABEL_PLACEHOLDER';

// TODO: jonasws Write a unit test for this "monster"
export const groupOrdersByLabel = (
  groupingLabel: string,
  orders: OrderDeepFragment[]
): Record<string, OrderDeepFragment[]> =>
  groupBy(orders, ({ labels }) => {
    const parsedLabels = parseLabels(labels);
    const matchingLabel = find(parsedLabels, ['key', groupingLabel]);
    if (matchingLabel) {
      return matchingLabel.value;
    } else {
      return NO_LABEL_PLACEHOLDER;
    }
  });

export function OrdersLoadingPanel() {
  const { t } = useTranslation('project');
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>{t('tables')}</PanelTitle>
      </PanelHeader>
      <PanelContent>
        <PlaceholderText as="span">{t('tables-panel.loading')}</PlaceholderText>
        <Spinner size="sm" color="brand.400"></Spinner>
      </PanelContent>
    </Panel>
  );
}

// eslint-disable-next-line complexity
export function OrdersPanel(props: OrdersPanelProps): React.ReactElement {
  const { diff, referenceState, isHistoryDrawerOpen } = useProjectHistory();

  const parseJson = useCallback((value: unknown): unknown => {
    if (typeof value === 'string') {
      try {
        return parseJson(JSON.parse(value));
      } catch {
        return value;
      }
    }
    if (Array.isArray(value)) return value.map(parseJson);
    if (value && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) result[k] = parseJson(v);
      return result;
    }
    return value;
  }, []);

  const orders = useMemo(() => {
    if (isHistoryDrawerOpen && referenceState && diff) {
      const historicalOrders = getHistoricalOrders(referenceState, diff);
      return historicalOrders.sort((a, b) => a.name.localeCompare(b.name));
    }
    return [...props.orders].sort((a, b) => a.name.localeCompare(b.name));
  }, [props.orders, isHistoryDrawerOpen, referenceState, diff]);

  const allLabelNames = useMemo(() => {
    return uniq(
      flatMap(orders, (order) => map(parseLabels(order.labels), 'key'))
    );
  }, [orders]);
  const groupingLabelKey = 'order-label-grouping-' + props.project.id;

  const [groupingLabel, setGroupingLabel] = useLocalStorage<string | null>(
    groupingLabelKey,
    null
  );

  const [isCreatingNewOrder, setIsCreatingNewOrder] = useState(false);

  const mixpanelProperties: EventProperties = useMemo(
    () => ({
      'Project Name': props.project.name,
      'Project ID': props.project.id,
    }),
    [props.project.id, props.project.name]
  );

  const { trackEvent } = useMixpanel();

  const {
    isOpen: orderModalIsOpen,
    onOpen: openOrderModal,
    onClose: closeOrderModal,
  } = useDisclosure();

  const {
    isOpen: buyModalIsOpen,
    onOpen: openBuyModal,
    onClose: closeBuyModal,
  } = useDisclosure();

  useHotkeys(
    'shift+t',
    () => {
      handleNewOrder();
    },
    {
      enabled: !orderModalIsOpen,
    }
  );

  const { t } = useTranslation(['common', 'project', 'paywall']);

  const navigate = useNavigate();

  const [, createOrder] = useMutation(CreateOrderDocument);
  const [, createTableFromTemplate] = useMutation(
    CreateTableFromTemplateDocument
  );
  const [, updateOrder] = useMutation(UpdateOrderDocument);
  const toast = useToast();
  const location = useLocation();
  const [newOrderId, setNewOrderId] = useState<string | null>(
    location.state?.newOrderId || null
  );

  const [{ data: projectTemplates }] = useQuery({
    query: GetProjectTemplatesPageDocument,
  });

  const { tenantTier, isProtan } = useUserTenant();
  const enablePayment = useFlag('enable-payments');
  const { viewerMode } = useViewerMode();

  const {
    setSelectedFolder,
    data: shapeFolders,
    createFolder,
  } = useShapeFolders();

  const { project } = props;

  const [{ data: sharedLinksData }] = useQuery({
    query: GetSharedLinksDocument,
    variables: { projectId: project.id },
    pause: !project.id,
  });

  const projectSharedLinks = useMemo<SharedLinkDeepFragment[]>(
    () => sharedLinksData?.sharedLinks || [],
    [sharedLinksData]
  );

  const TEMPLATE_TAG_ID = '3d21c9c3-3477-445c-8711-ed904435f802';
  const filteredOrders = useMemo(() => {
    const shouldRestrict =
      isProtan &&
      viewerMode === ViewerMode.SheetsV2 &&
      !project.tags.includes(TEMPLATE_TAG_ID);
    return shouldRestrict
      ? orders.filter((order) => order.isAggregatedOrder)
      : orders;
  }, [isProtan, viewerMode, project.tags, orders]);

  const getOrderDiff = useCallback(
    // eslint-disable-next-line complexity
    (order: OrderDeepFragment): OrderItemDiff => {
      const orderKey = `project.orders.${order.id}`;
      const nameKey = `${orderKey}.name`;
      const collectionKey = 'project.orders';

      if (diff?.[collectionKey]) {
        const collectionDiff = diff[collectionKey];
        const oldParsed = parseJson(collectionDiff.old);
        const newParsed = parseJson(collectionDiff.new);
        const oldHasOrder = Boolean(
          oldParsed &&
            typeof oldParsed === 'object' &&
            !Array.isArray(oldParsed) &&
            (oldParsed as Record<string, unknown>)[order.id]
        );
        const newHasOrder = Boolean(
          newParsed &&
            typeof newParsed === 'object' &&
            !Array.isArray(newParsed) &&
            (newParsed as Record<string, unknown>)[order.id]
        );
        if (newHasOrder && !oldHasOrder) {
          return {
            eventType: EventType.Created,
            newName: order.name,
          };
        }
        if (oldHasOrder && !newHasOrder) {
          return {
            eventType: EventType.Deleted,
            oldName: order.name,
          };
        }
      }

      if (diff?.[orderKey]) {
        const orderDiff = diff[orderKey];
        if (orderDiff.new && !orderDiff.old) {
          return {
            eventType: EventType.Created,
            newName: order.name,
          };
        }
        if (orderDiff.old && !orderDiff.new) {
          return {
            eventType: EventType.Deleted,
            oldName: order.name,
          };
        }
      }

      if (diff?.[nameKey]) {
        const nameDiff = diff[nameKey];
        return {
          eventType: EventType.Modified,
          oldName: nameDiff.old,
          newName: nameDiff.new,
        };
      }

      const hasOtherChanges = diff
        ? Object.keys(diff).some(
            (key) => key.startsWith(orderKey) && !key.endsWith('.name')
          )
        : false;
      if (hasOtherChanges) {
        return {
          eventType: EventType.Modified,
          oldName: order.name,
          newName: order.name,
        };
      }

      const hasEntryChanges = diff
        ? Object.keys(diff).some(
            (key) => key.includes('orderEntries') && key.includes(order.id)
          )
        : false;
      if (hasEntryChanges) {
        return {
          eventType: EventType.Modified,
          oldName: order.name,
          newName: order.name,
        };
      }

      return { oldName: order.name, newName: order.name };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diff]
  );

  // Function to configure column visibility and ordering for Protan aggregated orders
  type ProtanOrderData = {
    orderColumns: Array<{
      id: string;
      name: string;
      isVisible?: boolean;
      columnOrder?: number | null;
      columnType?: ColumnType | null;
    }>;
  };

  const configureProtanAggregatedOrderColumns = useCallback(
    async (orderId: string, orderData: ProtanOrderData) => {
      try {
        console.log('Order columns:', orderData.orderColumns);

        // Define the desired column configuration for Protan aggregated orders
        const protanColumnConfig = [
          { name: 'SKU', isVisible: true, columnOrder: 0 },
          { name: 'Description', isVisible: true, columnOrder: 1 },
          { name: 'Quantity', isVisible: true, columnOrder: 2 },
          { name: 'Markup [%]', isVisible: true, columnOrder: 3 },
          { name: 'Gross Quantity', isVisible: true, columnOrder: 4 },
          { name: 'Unit', isVisible: true, columnOrder: 5 },
        ];

        // Check which custom columns we need to create
        const existingColumnNames = orderData.orderColumns.map(
          (col) => col.name
        );
        const columnsToCreate = protanColumnConfig.filter(
          (config) => !existingColumnNames.includes(config.name)
        );

        // Resolve the default Quantity column ID to rename it to "Net Quantity"
        const quantityColumn = orderData.orderColumns.find(
          (c) => c.columnType === ColumnType.Quantity
        );

        const quantityNameMapping = quantityColumn
          ? {
              [quantityColumn.id]: {
                id: quantityColumn.id,
                newName: 'Net Quantity',
              },
            }
          : undefined;

        // First, create any missing custom columns and set the name mapping for Quantity
        if (columnsToCreate.length > 0 || quantityNameMapping) {
          const { error: createError } = await updateOrder({
            input: {
              id: orderId,
              patch: {
                ...(columnsToCreate.length > 0
                  ? {
                      orderColumns: {
                        create: columnsToCreate.map((config) => ({
                          name: config.name,
                          isVisible: config.isVisible,
                          columnType: ColumnType.Custom,
                          columnOrder: config.columnOrder,
                        })),
                      },
                    }
                  : {}),
                ...(quantityNameMapping
                  ? { columnNameMapping: quantityNameMapping }
                  : undefined),
              },
            },
          });

          if (createError) {
            console.error('Failed to create custom columns:', createError);
            return;
          }

          console.log('Successfully created custom columns');
        }

        // Now fetch the updated order to get all columns including the newly created ones
        // We'll need to wait a bit for the columns to be created
        setTimeout(async () => {
          try {
            // For now, let's just update the existing columns we can see
            const columnUpdates = orderData.orderColumns.map((column) => {
              // Find if this column matches our desired configuration
              const config = protanColumnConfig.find(
                (c) => c.name === column.name
              );

              if (config) {
                // This is one of our desired columns - make it visible and set order
                return {
                  id: column.id,
                  patch: {
                    isVisible: config.isVisible,
                    columnOrder: config.columnOrder,
                  },
                };
              } else {
                // This is not one of our desired columns - hide it
                return {
                  id: column.id,
                  patch: {
                    isVisible: false,
                  },
                };
              }
            });

            console.log('Column updates:', columnUpdates);

            // Update the order with the new column configuration using updateById
            const { error: updateError } = await updateOrder({
              input: {
                id: orderId,
                patch: {
                  orderColumns: {
                    updateById: columnUpdates,
                  },
                },
              },
            });

            if (updateError) {
              console.error(
                'Failed to configure Protan aggregated order columns:',
                updateError
              );
            } else {
              console.log(
                'Successfully configured Protan aggregated order columns'
              );
            }
          } catch (error) {
            console.error('Error in delayed column configuration:', error);
          }
        }, 500); // Wait 500ms for columns to be created
      } catch (error) {
        console.error(
          'Error configuring Protan aggregated order columns:',
          error
        );
      }
    },
    [updateOrder]
  );

  const onSubmitNewOrder = useCallback(
    // eslint-disable-next-line complexity
    async (name: string, labels: string, isAggregatedOrder?: boolean) => {
      const createOrderInput: CreateOrderInput = {
        order: {
          projectId: project.id,
          name,
          labels,
          status: OrderStatus.Open,
          isAggregatedOrder: isAggregatedOrder,
        },
      };

      if (isProtan && !isAggregatedOrder) {
        if (viewerMode === ViewerMode.SheetsV2) {
          const newOrderName =
            'Roof ' +
            (project.orders.filter((order) => order.isAggregatedOrder).length +
              1);

          const result = await createOrder({
            input: {
              ...createOrderInput,
              order: {
                ...createOrderInput.order,
                name: newOrderName,
                isAggregatedOrder: true,
                areItemsFlattened: true,
                aggregatedOrderFilters: [
                  {
                    columnName: 'Quantity',
                    columnType: ColumnType.Quantity,
                    predicate: PredicateType.GreaterThan,
                    predicateValues: ['0'],
                  },
                  {
                    columnName: 'Title',
                    columnType: ColumnType.Description,
                    predicate: PredicateType.Equals,
                    predicateValues: [newOrderName],
                  },
                  {
                    columnName: 'SKU',
                    columnType: ColumnType.Custom,
                    predicate: PredicateType.Exists,
                    predicateValues: [],
                  },
                ],
              },
            },
          });

          if (result.error) {
            toast({
              title: t('toast.error.create-table-title'),
              status: 'error',
            });
          } else {
            const newId = result.data?.createOrder?.order?.id;

            console.log('newId', newId);

            if (newId) {
              console.log('Creating Protan aggregated order');
              // Configure columns for Protan aggregated orders
              if (result.data?.createOrder?.order) {
                await configureProtanAggregatedOrderColumns(
                  newId,
                  result.data.createOrder.order
                );
              }

              // Create shape folder with the same name for Protan orders
              try {
                await createFolder(newOrderName);
                console.log(`Created shape folder: ${newOrderName}`);
              } catch (error) {
                console.error('Failed to create shape folder:', error);
                // Don't fail the order creation if shape folder creation fails
              }

              setNewOrderId(newId);
              // After creating a Protan order, navigate with an autoDraw hint for Sheets V2
              navigate(
                `/project/${project.id}/takeoff/${newId}?autoDraw=magicPolygon`
              );
              return newId;
            }
          }
        } else {
          const templateId =
            projectTemplates?.projectTemplatesConnection?.nodes.find(
              (template) => template.templateName === 'Protan Project Template'
            )?.id;
          if (templateId) {
            const result = await createTableFromTemplate({
              projectId: project.id,
              newName: 'Roof ' + (project.orders.length + 1),
              templateId: templateId,
            });

            if (result.error) {
              toast({
                title: t('toast.error.create-table-title'),
                status: 'error',
              });
            } else {
              const newId = result.data?.createTableFromTemplate?.order?.id;

              if (newId) {
                setNewOrderId(newId);
                navigate(`/project/${project.id}/takeoff/${newId}`);
                return newId;
              }
            }
          }
        }
      } else {
        const result = await createOrder({ input: createOrderInput });

        if (result.error) {
          toast({
            title: t('toast.error.create-table-title'),
            status: 'error',
          });
        } else {
          const newId = result.data?.createOrder?.order?.id;

          if (newId) {
            setNewOrderId(newId);
            navigate(`/project/${project.id}/takeoff/${newId}`);
          }
        }

        return result.data?.createOrder?.order?.id;
      }
    },
    [
      configureProtanAggregatedOrderColumns,
      createFolder,
      createOrder,
      createTableFromTemplate,
      isProtan,
      navigate,
      project.id,
      project.orders,
      projectTemplates?.projectTemplatesConnection?.nodes,
      t,
      toast,
      viewerMode,
    ]
  );

  const { logEvent } = useEventHistory(project.id);

  const maxTablesReached: boolean = useMemo(() => {
    if (props.project && props.project.orders) {
      const numberOfOrders = props.project.orders.length;
      return (
        tenantTier === 'FREE' &&
        enablePayment &&
        numberOfOrders >= tierLimits.free.MAX_TABLES
      );
    }
    return false;
  }, [enablePayment, props.project, tenantTier]);

  const handleNewOrder = (isAggregatedTable?: boolean) => {
    if (maxTablesReached) {
      openBuyModal();
      return;
    }
    if (allLabelNames.length > 0 && !isAggregatedTable && !isProtan) {
      openOrderModal();
      return;
    }

    setIsCreatingNewOrder(true);

    const newTableName = t('project:new-table-name');

    onSubmitNewOrder(newTableName, JSON.stringify([]), isAggregatedTable)
      .then((newId: string | undefined) => {
        if (!newId) {
          return;
        }

        trackEvent(EventName.TabledCreated, {
          ...mixpanelProperties,
          'Table Name': newTableName,
          'Is Reporting Table': isAggregatedTable,
        });

        logEvent({
          eventName: 'Table Created',
          eventType: EventType.Created,
          resourceName: newTableName,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [newId],
            details: `Created table ${newTableName}`,
          },
        });
      })
      .finally(() => {
        setIsCreatingNewOrder(false);
      });
  };

  const { isOpen: showProtanUploadModal, onClose: closeProtanUploadModal } =
    useDisclosure({ defaultIsOpen: true });
  const { projectSteps } = useProtanProjectSteps();
  const aiAnalysisStep = projectSteps?.find(
    (step) => step.step === ProtanProjectStepEnum.AiAnalysis
  );
  const protanFilesUploaded =
    aiAnalysisStep &&
    aiAnalysisStep?.status !== ProtanProjectStepStatusEnum.NotStarted;

  const enableAggregatedOrders = useFlag('enable-aggregated-orders');
  const params = useParams();

  useEffect(() => {
    if (
      isProtan &&
      viewerMode === ViewerMode.SheetsV2 &&
      shapeFolders &&
      shapeFolders.length > 0
    ) {
      const activeOrderName = orders.find(
        (order) => order.id === params.orderId
      )?.name;

      if (activeOrderName) {
        const activeFolder = shapeFolders.find(
          (folder) => folder.name === activeOrderName
        );

        if (activeFolder) {
          setSelectedFolder(activeFolder.id);
        }
      }
    }
  }, [
    shapeFolders,
    setSelectedFolder,
    isProtan,
    viewerMode,
    orders,
    params.orderId,
  ]);

  let ordersByGroupingLabelElement: React.ReactElement | null = null;
  if (groupingLabel === null) {
    ordersByGroupingLabelElement = (
      <Accordion
        variant="panel-accordion"
        data-testid="orders-panel-accordion"
        marginLeft={-2}
      >
        {filteredOrders.map((order) => (
          <OrderItem
            key={order.id}
            isActive={params.orderId === order.id}
            order={order}
            orders={orders}
            resolvedDbIds={props.resolvedDbIds}
            renameOnLoad={order.id === newOrderId}
            onRenameComplete={(orderId) => {
              if (newOrderId === orderId) {
                setNewOrderId(null);
              }
            }}
            readOnly={props.readOnly}
            projectSharedLinks={projectSharedLinks}
            isProtan={isProtan}
            shapeFolders={shapeFolders}
            isDiffing={isHistoryDrawerOpen}
            diff={getOrderDiff(order)}
          />
        ))}
      </Accordion>
    );
  } else {
    const ordersByGroupingLabel = groupOrdersByLabel(
      groupingLabel,
      filteredOrders
    );
    ordersByGroupingLabelElement = (
      <Accordion
        allowMultiple
        variant="panel-accordion"
        data-testid="orders-panel-accordion"
      >
        {Object.entries(ordersByGroupingLabel)
          .sort(([label1], [label2]) => label1.localeCompare(label2))
          .map(([label, orders]) => (
            <AccordionItem key={label}>
              <AccordionButton>
                <Box flex="1" textAlign="left" overflow="hidden">
                  {label === NO_LABEL_PLACEHOLDER ? (
                    <PlaceholderText>
                      {t('project:table-labels.no-value', {
                        label: groupingLabel,
                      })}
                    </PlaceholderText>
                  ) : (
                    <Text
                      overflow="hidden"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                    >
                      {label}
                    </Text>
                  )}
                </Box>
                <AccordionItem />
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pr={0} pb={1}>
                <Accordion allowToggle variant="panel-accordion">
                  {orders.map((order) => (
                    <OrderItem
                      key={order.id}
                      isActive={params.orderId === order.id}
                      order={order}
                      orders={orders}
                      resolvedDbIds={props.resolvedDbIds}
                      renameOnLoad={order.id === newOrderId}
                      onRenameComplete={(orderId) => {
                        if (newOrderId === orderId) {
                          setNewOrderId(null);
                        }
                      }}
                      readOnly={props.readOnly}
                      projectSharedLinks={projectSharedLinks}
                      isProtan={isProtan}
                      shapeFolders={shapeFolders}
                      isDiffing={isHistoryDrawerOpen}
                      diff={getOrderDiff(order)}
                    />
                  ))}
                </Accordion>
              </AccordionPanel>
            </AccordionItem>
          ))}
      </Accordion>
    );
  }

  return (
    <>
      {isProtan && !protanFilesUploaded ? (
        <ProtanUploadModal
          order={orders[0]}
          isOpen={showProtanUploadModal && isProtan}
          onClose={closeProtanUploadModal}
          projectId={project.id}
        />
      ) : null}
      <Panel>
        <PanelHeader
          hasNoMarginBottom={groupingLabel != null && orders.length > 0}
        >
          <PanelTitle>{t('tables')}</PanelTitle>
          <PanelActionButtons>
            {allLabelNames.length > 0 && !isProtan && (
              <Menu isLazy>
                <Tooltip label={t('table-labels.grouping-tooltip')}>
                  <MenuButton
                    as={IconButton}
                    isRound
                    aria-label="select grouping label"
                    colorScheme="gray"
                    size="xs"
                    icon={<SparkelIcon icon={faSitemap} />}
                  />
                </Tooltip>
                <Portal>
                  <MenuList zIndex={99}>
                    <MenuItem onClick={() => void setGroupingLabel(null)}>
                      {t('project:table-labels.no-grouping')}
                      {groupingLabel === null ? (
                        <SparkelIcon
                          style={{ paddingLeft: '4px' }}
                          icon={faCheck}
                        />
                      ) : null}
                    </MenuItem>
                    {allLabelNames.map((labelName) => (
                      <MenuItem
                        key={labelName}
                        onClick={() => void setGroupingLabel(labelName)}
                      >
                        {labelName}
                        {groupingLabel === labelName ? (
                          <SparkelIcon
                            style={{ paddingLeft: '4px' }}
                            icon={faCheck}
                          />
                        ) : null}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Portal>
              </Menu>
            )}
            {props.isContractor ? (
              <ButtonGroup
                isAttached
                colorScheme="brand"
                variant={'outline'}
                borderRadius="md"
              >
                <Tooltip label={t('project:new-table.button.tooltip')}>
                  <Button
                    aria-label="New table modal"
                    size="xs"
                    isLoading={isCreatingNewOrder}
                    onClick={() => handleNewOrder()}
                    borderRadius="md"
                  >
                    {t('project:new-table.button.new')}
                  </Button>
                </Tooltip>
                {enableAggregatedOrders && !isProtan && (
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      aria-label="add-table-options"
                      icon={
                        <SparkelIcon icon={faChevronDown} color={'inherit'} />
                      }
                    />
                    <Portal>
                      <MenuList
                        zIndex={99}
                        maxHeight={'50vh'}
                        maxWidth={'300px'}
                      >
                        <MenuItem
                          icon={
                            <SparkelIcon
                              icon={faTableList}
                              size="2xl"
                              color={'gray.500'}
                              style={{
                                marginLeft: '12px',
                                marginRight: '12px',
                              }}
                            />
                          }
                          onClick={() => handleNewOrder()}
                        >
                          <VStack align={'start'}>
                            <Heading size={'xs'} display={'block'}>
                              {t('project:new-table.standard-title')}
                            </Heading>
                            <Text fontSize={'xs'} color={'gray.500'}>
                              {t('project:new-table.standard-description')}
                            </Text>
                          </VStack>
                        </MenuItem>
                        <Divider />
                        <MenuItem
                          icon={
                            <SparkelIcon
                              icon={faRss}
                              size="2xl"
                              color={'gray.500'}
                              style={{
                                marginLeft: '12px',
                                marginRight: '12px',
                              }}
                            />
                          }
                          onClick={() => handleNewOrder(true)}
                        >
                          <VStack align={'start'}>
                            <Heading size={'xs'}>
                              {t('project:new-table.reporting-title') + ' '}
                            </Heading>
                            <Text fontSize={'xs'} color={'gray.500'}>
                              {t('project:new-table.reporting-description')}
                            </Text>
                          </VStack>
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                )}
              </ButtonGroup>
            ) : null}
          </PanelActionButtons>
        </PanelHeader>
        <PanelContent
          pl={groupingLabel != null && orders.length > 0 ? 0 : undefined}
        >
          {orders.length > 0 ? (
            ordersByGroupingLabelElement
          ) : (
            <PlaceholderText>
              {t('project:tables-panel.placeholder')}
            </PlaceholderText>
          )}
        </PanelContent>
      </Panel>
      {
        //Split out modal in own component, since labels are used a bit differently here now
      }
      <NewOrderModal
        project={props.project}
        orderModalIsOpen={orderModalIsOpen}
        onSubmitNewOrder={onSubmitNewOrder}
        closeOrderModal={closeOrderModal}
      ></NewOrderModal>
      <Modal
        isOpen={buyModalIsOpen}
        onClose={closeBuyModal}
        isCentered
        size={'full'}
      >
        <ModalOverlay />
        <Buy
          title={t('paywall:table-limit-reached.title')}
          subtitle={t('paywall:table-limit-reached.subtitle')}
          onClose={closeBuyModal}
        />
      </Modal>
    </>
  );
}
